/**
 * "What's on this date?" on /calendar/: the pure part. It merges one month's
 * festival rows (/calendar/month, festivals.yaml) with the same month's
 * vrat-year rows (/calendar/vrat-year: Ekadashi, Purnima, Amavasya, Pradosh,
 * Sankashti, Masik Shivratri, Sankranti), and answers one date from the result.
 *
 * Owner decisions (2026-10-20):
 * - The vrat-year rows join the grid and the answer: the page's WebApplication
 *   schema already says the calendar covers Purnima and Amavasya, which the
 *   festival list alone mostly lacks.
 * - A vrat-year Ekadashi, Pradosh or Sankashti row is dropped when the month
 *   already lists the same kind on the same date (date + kind).
 * - Rows on adjacent days are never merged. Sharad Purnima (Kojagari) is on
 *   25 October 2026 in the festival list; the engine's Ashvina Purnima row is
 *   dated 26 October with its fast kept on 25 October. Both show, each with its
 *   own fasting flag and note.
 * - Only the current month's merged rows go into the page's props; the year
 *   stays on the server.
 *
 * Applied on review (not part of the dated owner decision; listed for the
 * owner in the PR):
 * - A Purnima, Amavasya, Masik Shivratri or Sankranti row on the same date as a
 *   festival-list row of the same kind is dropped only when it adds nothing to
 *   that row: no fast the listed row lacks, and no fast kept on another day.
 *   Maha Shivratri and Makar Sankranti show once; Guru Purnima keeps the
 *   engine's Purnima row because that row carries the Purnima fast.
 * - A vrat-year label part that names a festival with its own festival-list
 *   row is dropped ("Kartik Amavasya / Diwali Lakshmi Puja" reads "Kartik
 *   Amavasya"): the festival's own row carries its name, its date and its
 *   guide, so the tithi row never dates that festival to a different day.
 */

import type { CalendarTodayResponse } from "@/lib/api/calendar";
import { formatDayMonth, formatDateLong } from "@/components/calendar/types";
import { timingDateBounds, timingHubHref, isValidCivilDate } from "@/lib/timing-city-param";

/** The page's range: the "2026-2027" its title promises, and the month navigation's bounds. */
export const CALENDAR_DATE_MIN = "2026-01-01";
export const CALENDAR_DATE_MAX = "2027-12-31";

/** The fields the calendar renders from an engine row (the backend's FestivalSummary). */
export interface CalendarDateRow {
  id: string;
  name_en: string;
  name_hi?: string;
  type: string;
  /** YYYY-MM-DD. */
  date: string;
  fasting?: boolean;
  /** When the fast is kept, if the engine names a day (it can be the day before). */
  fasting_date?: string | null;
  description_en?: string;
}

/** What the server hands the calendar: India's today, and the reads it could make. */
export interface CalendarInitial {
  /** Today in India, YYYY-MM-DD. */
  today: string;
  year: number;
  month: number;
  /** /calendar/today for `today`, or null when it could not be read. */
  todayData: CalendarTodayResponse | null;
  /**
   * The current month's rows, or null when the festival list could not be
   * read. Without the year's vrat dates they are the festival list alone.
   */
  monthRows: CalendarDateRow[] | null;
  /** False when `monthRows` lack the vrat-year rows (that read failed). */
  monthComplete: boolean;
}

export type ObservanceKind =
  "ekadashi" | "pradosh" | "sankashti" | "purnima" | "amavasya" | "masik_shivratri" | "sankranti";

/**
 * The vrat-year groups, in the order their rows follow a day's festival rows.
 * @internal
 */
export const VRAT_YEAR_KINDS: readonly ObservanceKind[] = [
  "ekadashi",
  "purnima",
  "amavasya",
  "pradosh",
  "sankashti",
  "masik_shivratri",
  "sankranti",
];

// festivals.yaml ids name their kind: "papankusha_ekadashi",
// "pradosh_vrat_oct_shukla_2026", "sankashti_chaturthi_oct",
// "mahalaya_amavasya", "sharad_purnima", "makar_sankranti", and
// "maha_shivaratri" (the vrat-year's Masik Shivratri group names that month's
// Shivratri "Maha Shivratri").
const KIND_MARKERS: readonly (readonly [ObservanceKind, string])[] = [
  ["ekadashi", "ekadashi"],
  ["pradosh", "pradosh"],
  ["sankashti", "sankashti"],
  ["purnima", "purnima"],
  ["amavasya", "amavasya"],
  ["masik_shivratri", "shivratri"],
  ["masik_shivratri", "shivaratri"],
  ["sankranti", "sankranti"],
];

/** The kinds whose vrat-year row always yields to a same-date festival-list row. */
const ALWAYS_COLLAPSED: ReadonlySet<ObservanceKind> = new Set(["ekadashi", "pradosh", "sankashti"]);

// Vrat-year label parts (either side of " / ") that name a festival with its
// own festivals.yaml row: buddha_purnima, hanuman_jayanti, raksha_bandhan,
// dev_diwali, holika_dahan, mauni_amavasya, diwali. Vat Purnima, Vat Savitri,
// Shakambhari, Hariyali Amavasya and Kushotpatini have no row of their own and
// stay.
const FESTIVAL_LABEL_PARTS: ReadonlySet<string> = new Set([
  "Buddha Purnima",
  "Hanuman Jayanti",
  "Raksha Bandhan",
  "Dev Diwali",
  "Holika Dahan",
  "Mauni Amavasya",
  "Diwali Lakshmi Puja",
]);

/** The observance kind a festival-list id names, or null (Diwali, Dussehra, ...). */
export function monthRowKind(id: string): ObservanceKind | null {
  for (const [kind, marker] of KIND_MARKERS) {
    if (id.includes(marker)) return kind;
  }
  return null;
}

function trimRow(row: CalendarDateRow): CalendarDateRow {
  return {
    id: row.id,
    name_en: row.name_en,
    name_hi: row.name_hi,
    type: row.type,
    date: row.date,
    fasting: row.fasting,
    fasting_date: row.fasting_date ?? null,
    description_en: row.description_en,
  };
}

function monthPrefix(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}-`;
}

function hasString(value: object, key: string): boolean {
  return key in value && typeof Reflect.get(value, key) === "string";
}

function isRow(value: unknown): value is CalendarDateRow {
  if (typeof value !== "object" || value === null) return false;
  if (!["id", "name_en", "type", "date"].every((key) => hasString(value, key))) return false;
  return isValidCivilDate(String(Reflect.get(value, "date")));
}

/** The well-formed rows of an engine list; anything else is dropped. */
export function engineRows(value: unknown): CalendarDateRow[] {
  return Array.isArray(value) ? value.filter(isRow) : [];
}

/** One year's vrat-year groups, by kind; a missing or malformed group is empty. */
export type VratYearRows = Record<ObservanceKind, CalendarDateRow[]>;

function byKind(rows: (kind: ObservanceKind) => CalendarDateRow[]): VratYearRows {
  return {
    ekadashi: rows("ekadashi"),
    purnima: rows("purnima"),
    amavasya: rows("amavasya"),
    pradosh: rows("pradosh"),
    sankashti: rows("sankashti"),
    masik_shivratri: rows("masik_shivratri"),
    sankranti: rows("sankranti"),
  };
}

/**
 * The engine's `vratas` object (keyed exactly like VRAT_YEAR_KINDS), validated.
 * @internal
 */
export function vratYearRows(vratas: unknown): VratYearRows {
  return byKind((kind) =>
    typeof vratas === "object" && vratas !== null && kind in vratas
      ? engineRows(Reflect.get(vratas, kind))
      : []
  );
}

/** The vrat-year rows that fall in one month, trimmed. */
export function vratRowsInMonth(vrat: VratYearRows, year: number, month: number): VratYearRows {
  const prefix = monthPrefix(year, month);
  return byKind((kind) => vrat[kind].filter((row) => row.date.startsWith(prefix)).map(trimRow));
}

/** No vrat-year rows: the merge then returns the festival list alone. */
export const NO_VRAT_ROWS: VratYearRows = byKind(() => []);

/**
 * A /calendar/vrat-year answer for `year`, validated, or null. A year with no
 * vrat date at all is a broken answer, not an empty year: the server and the
 * browser both treat it as a failed read, so the answer box never calls a day
 * empty when the vrat dates are missing.
 */
export function vratYearOrNull(response: unknown, year: number): VratYearRows | null {
  if (typeof response !== "object" || response === null) return null;
  if (Reflect.get(response, "year") !== year) return null;
  const rows = vratYearRows(Reflect.get(response, "vratas"));
  return VRAT_YEAR_KINDS.some((kind) => rows[kind].length > 0) ? rows : null;
}

/**
 * "Kartik Amavasya / Diwali Lakshmi Puja" -> "Kartik Amavasya" (see FESTIVAL_LABEL_PARTS).
 * @internal
 */
export function tithiLabel(name: string): string {
  const parts = name.split(" / ");
  const kept = parts.filter((part) => !FESTIVAL_LABEL_PARTS.has(part));
  return kept.length > 0 && kept.length < parts.length ? kept.join(" / ") : name;
}

// The vrat-year row tells the seeker nothing the listed row does not: no fast
// the listed row lacks, and no fast kept on another day.
function addsNothing(vratRow: CalendarDateRow, listed: CalendarDateRow): boolean {
  if (!vratRow.fasting) return true;
  if (vratRow.fasting_date && vratRow.fasting_date !== vratRow.date) return false;
  return listed.fasting === true;
}

// The festival list's rows by "date|kind", for the rows that name a kind.
function listedByDateAndKind(own: readonly CalendarDateRow[]): Map<string, CalendarDateRow[]> {
  const listed = new Map<string, CalendarDateRow[]>();
  for (const row of own) {
    const kind = monthRowKind(row.id);
    if (!kind) continue;
    const key = `${row.date}|${kind}`;
    listed.set(key, [...(listed.get(key) ?? []), row]);
  }
  return listed;
}

// Whether a vrat-year row gives way to the same date's listed rows of its kind.
function yieldsToListed(
  row: CalendarDateRow,
  kind: ObservanceKind,
  same: readonly CalendarDateRow[]
): boolean {
  if (same.length === 0) return false;
  return ALWAYS_COLLAPSED.has(kind) || same.some((listed) => addsNothing(row, listed));
}

/**
 * One month's rows: the festival list's rows, then each vrat-year row the
 * month does not already list (see the header for the same-date rule). Sorted
 * by date; within a date the festival rows keep their order and come first.
 */
export function mergeMonthRows(
  monthRows: readonly CalendarDateRow[],
  vrat: VratYearRows,
  year: number,
  month: number
): CalendarDateRow[] {
  const prefix = monthPrefix(year, month);
  const own = monthRows.filter((row) => row.date.startsWith(prefix)).map(trimRow);
  const listed = listedByDateAndKind(own);
  const added = new Set<string>();
  const rows: CalendarDateRow[] = [];
  for (const kind of VRAT_YEAR_KINDS) {
    for (const row of vrat[kind]) {
      const key = `${row.date}|${kind}`;
      if (!row.date.startsWith(prefix) || added.has(key)) continue;
      if (yieldsToListed(row, kind, listed.get(key) ?? [])) continue;
      added.add(key);
      rows.push({ ...trimRow(row), name_en: tithiLabel(row.name_en) });
    }
  }
  // Array sort is stable: within a date, festival rows stay first, in order.
  return [...own, ...rows].sort((a, b) => a.date.localeCompare(b.date));
}

/** Every row dated `date`. */
export function observancesOn(rows: readonly CalendarDateRow[], date: string): CalendarDateRow[] {
  return rows.filter((row) => row.date === date);
}

/**
 * Rows dated another day whose fast the engine keeps on `date`: picking
 * 25 October 2026 names the Ashvina Purnima fast the 26 October row keeps on
 * the 25th. Only the month's own rows are searched (in 2026-2027 no such fast
 * crosses a month boundary).
 */
export function fastsKeptOn(rows: readonly CalendarDateRow[], date: string): CalendarDateRow[] {
  return rows.filter(
    (row) => row.fasting === true && row.fasting_date === date && row.date !== date
  );
}

/** The note on a row from fastsKeptOn: "Fast kept on this date (listed on 26 October)". */
export function keptFastNote(row: CalendarDateRow): string {
  return `Fast kept on this date (listed on ${formatDayMonth(row.date)})`;
}

/**
 * Whether any row's fast is kept on `date` (its own date unless the engine names another).
 * @internal
 */
export function isFastOn(rows: readonly CalendarDateRow[], date: string): boolean {
  return rows.some((row) => row.fasting === true && (row.fasting_date || row.date) === date);
}

/**
 * The month rows the Today card adds to /calendar/today, which reads the
 * festival list only: today's vrat-year rows, and fasts kept today for a row
 * dated another day. The card and the answer box below it then agree.
 */
export function todayCardExtras(
  todayData: CalendarTodayResponse,
  rows: readonly CalendarDateRow[],
  today: string
): { observances: CalendarDateRow[]; fastsKept: CalendarDateRow[]; fasting: boolean } {
  // The browser's /calendar/today answer is not validated upstream: a missing
  // group must not break the card.
  const groups: unknown[] = [
    todayData.festivals,
    todayData.vrats,
    todayData.eclipses,
    todayData.tithi_events,
  ];
  const shown = new Set(
    groups
      .flatMap((group) => (Array.isArray(group) ? group : []))
      .map((event: unknown) =>
        typeof event === "object" && event !== null ? Reflect.get(event, "id") : null
      )
  );
  return {
    observances: observancesOn(rows, today).filter((row) => !shown.has(row.id)),
    fastsKept: fastsKeptOn(rows, today),
    fasting: todayData.is_fasting_day === true || isFastOn(rows, today),
  };
}

/** "Fast kept on 25 October" when the engine dates the fast to another day, else null. */
export function fastingNote(row: CalendarDateRow): string | null {
  if (!row.fasting || !row.fasting_date || row.fasting_date === row.date) return null;
  return `Fast kept on ${formatDayMonth(row.fasting_date)}`;
}

/** The YYYY-MM key the client keeps a month's rows under. */
export function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** The year and month of a YYYY-MM-DD date. */
export function yearMonthOf(date: string): { year: number; month: number } {
  return { year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) };
}

/** A real date inside the page's range: what the date input may answer. */
export function isCalendarDate(value: string): boolean {
  return isValidCivilDate(value) && value >= CALENDAR_DATE_MIN && value <= CALENDAR_DATE_MAX;
}

/** The sentence for a date with no row. */
export function emptyDaySentence(date: string): string {
  return `No festival or vrat in this calendar falls on ${formatDateLong(date)}.`;
}

/**
 * The Panchang for `date`: the hub with `?date=` (canonical to the bare hub,
 * the same view its own date picker opens), or the bare hub for today or a day
 * outside its window.
 */
export function panchangHref(date: string, today: string): string {
  const { min, max } = timingDateBounds(today);
  const linkable = date !== today && date >= min && date <= max;
  return timingHubHref("panchang", "en", { date: linkable ? date : null });
}
